/**
 * Is this tab running the build the server is serving right now?
 *
 * A phone reopens the app and loads the page fresh, so it always gets the
 * newest build. A laptop tab stays open for days across several deploys and
 * keeps running yesterday's bundle against today's API, which looks like the
 * app "bugging out" (owner, Oct 20 2026). The service worker only reloads the
 * page when sw.js itself changes, which it almost never does.
 *
 * So: whenever the tab comes back to the front, fetch the live index.html
 * (bypassing the offline cache) and compare the main bundle it names with the
 * one running here. If they differ, reload — but not while someone is typing.
 */
const CHECK_EVERY_MS = 60_000;
const BUNDLE_RE = /\/static\/js\/main\.[a-z0-9]+\.js/i;

let lastCheck = 0;
let checking = false;

function runningBundle(): string | null {
  for (const s of Array.from(document.scripts)) {
    const m = s.src && s.src.match(BUNDLE_RE);
    if (m) return m[0];
  }
  return null;
}

function typing(): boolean {
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
}

async function check(force = false) {
  if (checking) return;
  if (!force && Date.now() - lastCheck < CHECK_EVERY_MS) return;
  const mine = runningBundle();
  if (!mine) return;
  checking = true;
  lastCheck = Date.now();
  try {
    // `live=1` makes the service worker step aside (see public/sw.js) so this
    // is the server's answer, not the cached shell.
    const res = await fetch(`/index.html?live=1&t=${Date.now()}`, { cache: 'no-store', credentials: 'same-origin' });
    if (!res.ok) return;
    const html = await res.text();
    const theirs = html.match(BUNDLE_RE)?.[0];
    if (!theirs || theirs === mine) return;
    if (typing()) {
      // Try again once they have moved on; never yank a half-typed form.
      lastCheck = 0;
      return;
    }
    // Guard against a reload loop if the server keeps naming a bundle it then
    // cannot serve: one reload per new bundle name, remembered for this tab.
    const key = 'kam-reloaded-for';
    if (sessionStorage.getItem(key) === theirs) return;
    sessionStorage.setItem(key, theirs);
    window.location.reload();
  } catch {
    // Offline or mid-deploy: nothing to do, the next focus will ask again.
  } finally {
    checking = false;
  }
}

/** Call once at startup, production only. */
export function watchForNewBuild() {
  document.addEventListener('visibilitychange', () => { if (!document.hidden) check(); });
  window.addEventListener('focus', () => check());
  // A tab that is never backgrounded (a laptop with the app on its own
  // screen) still asks every few minutes.
  setInterval(() => { if (!document.hidden) check(); }, 5 * CHECK_EVERY_MS);
}
